/**
 * Central status configuration for the 6-status workflow.
 *
 * Single source of truth for status colors, icons, labels, and utility functions.
 */

import {
  CheckCircle2,
  CircleDashed,
  Eye,
  RotateCcw,
  Timer,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

export type TestCaseStatus =
  | 'new'
  | 'ongoing'
  | 'errors'
  | 'retest'
  | 'ready_to_validate'
  | 'closed';

export interface StatusConfig {
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  /** Tailwind classes for badge background + text */
  badgeClasses: string;
  /** HSL color string for charts and progress bars */
  color: string;
  /** Tailwind bg class for progress bar segments */
  barClass: string;
}

// =============================================================================
// Legacy Mapping
// =============================================================================

export const LEGACY_STATUS_MAP: Record<string, TestCaseStatus> = {
  pending: 'new',
  in_progress: 'ongoing',
  completed: 'closed',
  blocked: 'errors',
};

export const DONE_STATUSES: Set<TestCaseStatus> = new Set([
  'ready_to_validate',
  'closed',
]);

// =============================================================================
// Status Configuration
// =============================================================================

const STATUS_CONFIG: Record<TestCaseStatus, StatusConfig> = {
  new: {
    label: 'New',
    shortLabel: 'New',
    icon: CircleDashed,
    badgeClasses: 'bg-muted text-muted-foreground',
    color: 'hsl(var(--muted-foreground))',
    barClass: 'bg-muted-foreground/40',
  },
  ongoing: {
    label: 'Ongoing',
    shortLabel: 'Ongoing',
    icon: Timer,
    badgeClasses: 'bg-info/10 text-info',
    color: 'hsl(var(--info))',
    barClass: 'bg-info',
  },
  errors: {
    label: 'Errors',
    shortLabel: 'Errors',
    icon: XCircle,
    badgeClasses: 'bg-destructive/10 text-destructive',
    color: 'hsl(var(--destructive))',
    barClass: 'bg-destructive',
  },
  retest: {
    label: 'Retest',
    shortLabel: 'Retest',
    icon: RotateCcw,
    badgeClasses: 'bg-warning/10 text-warning',
    color: 'hsl(var(--warning))',
    barClass: 'bg-warning',
  },
  ready_to_validate: {
    label: 'Ready to Validate',
    shortLabel: 'Validate',
    icon: Eye,
    badgeClasses: 'bg-review/10 text-review',
    color: 'hsl(var(--review))',
    barClass: 'bg-review',
  },
  closed: {
    label: 'Closed',
    shortLabel: 'Closed',
    icon: CheckCircle2,
    badgeClasses: 'bg-success/10 text-success',
    color: 'hsl(var(--success))',
    barClass: 'bg-success',
  },
};

// Ordered array for consistent rendering (progress bar segments, legends)
export const STATUS_ORDER: TestCaseStatus[] = [
  'closed',
  'ready_to_validate',
  'ongoing',
  'retest',
  'errors',
  'new',
];

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get the full status config for a status value.
 * Handles legacy values transparently.
 */
export function getStatusConfig(status: string): StatusConfig {
  const normalized = normalizeStatus(status);
  return STATUS_CONFIG[normalized];
}

/**
 * Raw Excel status values grouped by canonical status.
 * Used by normalizeStatus to map any Excel value to one of the 6 statuses.
 */
const EXCEL_STATUS_MAP: Array<[Set<string>, TestCaseStatus]> = [
  [new Set(['closed', '05_closed', '06_closed']), 'closed'],
  [new Set(['pass', 'passed', 'ok', 'success', 'successful', '05_ready to validate', 'ready to validate', 'ready_to_validate']), 'ready_to_validate'],
  [new Set(['fail', 'failed', 'ko', 'error', 'nok', '03_errors', 'errors']), 'errors'],
  [new Set(['ongoing', '02_ongoing', '03_ongoing', 'in progress', 'in_progress']), 'ongoing'],
  [new Set(['retest', '04_retest', 'blocked', 'block', 'hold', 'on hold']), 'retest'],
  [new Set(['new', '01_new', 'not executed', 'not_executed', 'pending']), 'new'],
];

/**
 * Normalize a status string to a canonical 6-status value.
 * Handles canonical values, legacy 4-status values, and raw Excel values.
 */
export function normalizeStatus(status: string): TestCaseStatus {
  // Fast path: already canonical
  if (status in STATUS_CONFIG) {
    return status as TestCaseStatus;
  }

  // Legacy 4-status mapping
  if (status in LEGACY_STATUS_MAP) {
    return LEGACY_STATUS_MAP[status];
  }

  // Raw Excel value mapping (case-insensitive)
  const lower = (status || '').toLowerCase().trim();
  for (const [values, canonical] of EXCEL_STATUS_MAP) {
    if (values.has(lower)) {
      return canonical;
    }
  }

  return 'new';
}

/**
 * Calculate progress percentage from status counts.
 * Progress = (ready_to_validate + closed) / total * 100
 */
export function calculateProgress(stats: {
  ready_to_validate: number;
  closed: number;
  total: number;
}): number {
  if (stats.total === 0) return 0;
  const done = stats.ready_to_validate + stats.closed;
  return Math.round((done / stats.total) * 100 * 100) / 100;
}

export { STATUS_CONFIG };
